import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import bgVideo from '../../Assets/Img/outfits-banner-video.mp4';
import SkeletonCard from '../../Components/SkeletonCard';
import { TryOnButton } from '../../Components/TryOnButton/TryOnButton';
import { Container } from '../../Components/ui/Container/Container';
import Section from '../../Components/ui/Section/Section';
import { Typography } from '../../Components/ui/Typography/Typography';
import { useTrackScreenView } from '../../Hooks/use-track-screen-view';
import { garmentService } from '../../Services';
import { Outfit } from '../../Services/garmentService';
import styles from './index.module.scss';

type GenderFilter = 'all' | 'men' | 'women';

const outfitHasGender = (outfit: Outfit, gender: 'men' | 'women') =>
    outfit.garments.some((garment) =>
        garment.tags.some((tag) => tag.toLowerCase() === gender),
    );

export const Outfits = () => {
    useTrackScreenView('screen_outfits_view');

    return (
        <>
            <Section top="sm" bottom="sm">
                <Container>
                    <Section top="none" bottom="sm" className={styles.headerSection}>
                        <div className={styles.background}>
                            <video autoPlay loop muted playsInline className={styles.backgroundImage}>
                                <source src={bgVideo} type="video/mp4" />
                            </video>
                        </div>
                        <div className={styles.overlay} />
                        <Typography variant="heading1" component="h1" className={styles.header}>
                            Outfits
                        </Typography>
                    </Section>
                    
                    <OutfitsList />
                </Container>
            </Section>
        </>
    );
};

const OutfitsList = () => {
    const [filter, setFilter] = useState<GenderFilter>('all');
    const { isPending, isError, data, error } = useQuery({
        queryKey: ['outfits', 'all'],
        queryFn: () => garmentService.fetchAllOutfits(),
    });

    if (isPending) {
        return (
            <div className={styles.list}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonCard key={index} />
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="container">
                <span>Error: {error.message}</span>
            </div>
        );
    }

    const availableOutfits = data?.outfits.filter((outfit) => outfit.status === 'AVAILABLE') ?? [];

    if (availableOutfits.length === 0) {
        return (
            <div className="container">
                <Typography>No outfits available.</Typography>
            </div>
        );
    }

    const hasMen = availableOutfits.some((outfit) => outfitHasGender(outfit, 'men'));
    const hasWomen = availableOutfits.some((outfit) => outfitHasGender(outfit, 'women'));
    const showFilters = hasMen && hasWomen;

    const filteredOutfits =
        !showFilters || filter === 'all'
            ? availableOutfits
            : availableOutfits.filter((outfit) => outfitHasGender(outfit, filter));

    return (
        <>
            {showFilters ? (
                <ToggleGroup.Root
                    type="single"
                    value={filter}
                    onValueChange={(value) => {
                        if (value) setFilter(value as GenderFilter);
                    }}
                    aria-label="Filter outfits by gender"
                    className={styles.filter}
                >
                    <ToggleGroup.Item value="all" className={styles.filterButton}>
                        View both
                    </ToggleGroup.Item>
                    <ToggleGroup.Item value="women" className={styles.filterButton}>
                        Women
                    </ToggleGroup.Item>
                    <ToggleGroup.Item value="men" className={styles.filterButton}>
                        Men
                    </ToggleGroup.Item>
                </ToggleGroup.Root>
            ) : null}
            <div className={styles.list}>
                {filteredOutfits.map((outfit) => (
                    <OutfitCard key={outfit.id} outfit={outfit} />
                ))}
            </div>
        </>
    );
};

interface OutfitCardProps {
    outfit: Outfit;
}

const OutfitCard = ({ outfit }: OutfitCardProps) => {
    return (
        <div className={styles.card} style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className={styles.info}>
                <Typography variant="heading4">{outfit.name}</Typography>
            </div>
            <div className={styles.thumbnails}>
                {outfit.garments.map((g) => {
                    const outfitVariant = outfit.variants.find((v) => v.garment_id === g.id);
                    const matchedVariant =
                        g.variants.find((v) => v.color.name === outfitVariant?.variant_key.color) ??
                        g.variants[0];
                    const flatImage = matchedVariant?.images?.find((img) => img.tag === 'flat');
                    const url = (flatImage ?? matchedVariant?.images?.[0])?.url;
                    return <img key={g.id} src={url} alt={g.title} className={styles.thumbnail} />;
                })}
            </div>
            <div className={styles.cardFooter}>
                <div>See the full look on you instantly.</div>
                <TryOnButton
                    text="Try This Look"
                    className={styles.outfitButton}
                    garments={outfit.variants.map((variant) => {
                        const garment = outfit.garments.find(
                            (g) => g.id === variant.garment_id
                        );
                        const matchedVariant =
                            garment?.variants.find(
                                (v) =>
                                    v.color.name === variant.variant_key.color &&
                                    v.style === variant.variant_key.style &&
                                    v.fit === variant.variant_key.fit &&
                                    v.gender === variant.variant_key.gender
                            ) ?? garment?.variants[0];
                        return {
                            garmentId: variant.garment_id,
                            variant: variant.variant_key,
                            price: matchedVariant?.price?.amount,
                            availableSizes:
                                matchedVariant?.size_groups?.flatMap((group) => group.sizes) ?? [],
                        };
                    })}
                    partnerId={outfit.garments[0].partner_id}
                />
            </div>
        </div>
    );
};
